import * as React from 'react';
// mui
import {
  IconButton,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  DialogActions,
  useTheme,
  useMediaQuery,
  Tooltip,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
// icons
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import MoreVertIcon from '@mui/icons-material/MoreVert';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectEventById } from '../../../store/selectors/eventSelectors';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import { clearExcuse as clearEventExcuse } from '../../../store/slices/eventsSlice';
// types
import Event from '../../../interfaces/Event.interface';
import Task from '../../../interfaces/Task.interface';
// axios
import api from '../../../axios';
import { ClearIcon } from '@mui/x-date-pickers';

interface EventItemActionsProps {
  eventId: number;
  parentTaskId: number | null;
  isHovered: boolean;
}

export default function EventItemActions(props: EventItemActionsProps) {
  // props
  const eventId = props.eventId;
  const parentTaskId: number | null = props.parentTaskId;
  const isHovered = props.isHovered;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // redux
  const localEvent: Event = useSelector(selectEventById(eventId)) as Event;
  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const localParentTask: Task | null = parentTaskRaw ?? null;

  // state
  const [ confirmDeleteDialogStatus, setConfirmDeleteDialogStatus ] = React.useState(false);
  const [ confirmDeleteLoading, setConfirmDeleteLoading ] = React.useState(false);
  const [ mobileMenuAnchorEl, setMobileMenuAnchorEl ] = React.useState<null | HTMLElement>(null);

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
  };
  
  const handleMobileMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(null);
  };

  const handleLocalCompleteToggle = () => {
    api.put(`/events/update/${localEvent.id}`, {
      completed: !localEvent.completed,
    })
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const handleLocalConfirmDeleteButtonClick = () => {
    setConfirmDeleteLoading(true);

    api.delete(`/events/delete/${eventId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          setConfirmDeleteLoading(false);
          setConfirmDeleteDialogStatus(false);
        } else {
          console.error('Something went wrong with deleting an event!');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setConfirmDeleteLoading(false);
      })
  }

  const handleRemoveExcuse = () => {
    api.delete(`/events/excuses/remove/event/${eventId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          dispatch(clearEventExcuse(eventId));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  return (
    <React.Fragment>
      { isMobile ? (
        // Mobile menu
        <React.Fragment>
          <IconButton
            onClick={handleMobileMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={handleMobileMenuClose}>
            { (!localEvent.completed && localEvent.excused) && (
              <MenuItem
                onClick={(e) => {
                  handleMobileMenuClose(e);
                  handleRemoveExcuse();
                }}
              >
                <ListItemIcon>
                  <ClearIcon />
                </ListItemIcon>
                <ListItemText primary="Remove Excuse" />
              </MenuItem>
            )}
            
            { !localParentTask?.completed && (
              <MenuItem
                onClick={(e) => {
                  handleMobileMenuClose(e);
                  handleLocalCompleteToggle();
                }}
              >
                <ListItemIcon>
                  {localEvent.completed ? (
                    <ReplayIcon />
                  ) : (
                    <DoneIcon color={localParentTask?.completed ? 'disabled' : 'primary'} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={localEvent.completed ? 'Undo Event Completion' : 'Complete Event'}
                />
              </MenuItem>
            )}
      
            <MenuItem
              onClick={(e) => {
                handleMobileMenuClose(e);
                setConfirmDeleteDialogStatus(true);
              }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary="Delete Event" />
            </MenuItem>
          </Menu>
        </React.Fragment>
      ) : (
        // dektop buttons
        <React.Fragment>
          { isHovered && ( // display only if hovered
            <React.Fragment>
              { (!localEvent.completed && localEvent.excused) &&
                <Tooltip title="Remove Excuse">
                  <IconButton
                    sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                    onClick={(e) => { e.stopPropagation(); handleRemoveExcuse(); }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              }
              
              {/* complete / uncomplete button */}
              { !localParentTask?.completed &&
                <Tooltip
                  title={localEvent.completed ? 'Undo Event Completion' : 'Complete Event'}
                >
                  <span>
                    <IconButton
                      sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                      onClick={(e) => { e.stopPropagation(); handleLocalCompleteToggle(); }}
                    >
                      { localEvent.completed ?
                        <ReplayIcon />
                      :
                        <DoneIcon color={localParentTask?.completed ? 'disabled' : 'primary'} />
                      }
                    </IconButton>
                  </span>
                </Tooltip>
              }
              
              {/* delete button */}
              <IconButton
                sx={{ color: 'lightgrey' }}
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(true); }}
              >
                <DeleteIcon />
              </IconButton>
            </React.Fragment>
          )}
        </React.Fragment>
      )}

      {/* dialog to confirm task deletion */}
      <Dialog
        open={confirmDeleteDialogStatus}
        onClose={(e: any) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false); }}
      >
        <DialogTitle>
          Delete "{localEvent.title}"?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting this event will be permanent!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false) }}>Cancel</Button>
          <Button loading={confirmDeleteLoading} onClick={(e) => { e.stopPropagation(); handleLocalConfirmDeleteButtonClick(); }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}